import { Check, Circle, Loader2 } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export type ExtractionProgressStepStatus =
  | 'pending'
  | 'active'
  | 'waiting'
  | 'done'

export type ExtractionProgressStep = {
  id: string
  title: string
  subtitle?: string
  status: ExtractionProgressStepStatus
  /** Nested checklist rows (e.g. analysis under a search step). */
  children?: ExtractionProgressStep[]
}

type ExtractionProgressChecklistProps = {
  steps: ExtractionProgressStep[]
  /** Label for screen readers only */
  ariaLabel: string
  className?: string
}

export function ExtractionProgressChecklist({
  steps,
  ariaLabel,
  className,
}: ExtractionProgressChecklistProps) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-card px-4 py-3 text-sm shadow-sm',
        className
      )}
      role='list'
      aria-label={ariaLabel}
    >
      <ul className='space-y-3'>
        {steps.map((step) => (
          <ProgressStepRow key={step.id} step={step} nested={false} />
        ))}
      </ul>
    </div>
  )
}

export function ProgressStepRow({
  step,
  nested,
}: {
  step: ExtractionProgressStep
  nested: boolean
}) {
  const hasChildren = step.children != null && step.children.length > 0

  return (
    <li className='flex gap-3' role='listitem'>
      <span className='mt-0.5 shrink-0' aria-hidden>
        <StepIcon status={step.status} />
      </span>
      <div className='min-w-0 flex-1 translate-y-0.5 space-y-0.5'>
        <p
          className={cn(
            'leading-snug font-medium',
            step.status === 'pending' && 'text-muted-foreground',
            step.status === 'done' && 'text-muted-foreground',
            nested && 'text-[13px]'
          )}
          {...(step.status === 'active' || step.status === 'waiting'
            ? { 'aria-current': 'step' as const }
            : {})}
        >
          {step.title}
        </p>
        {step.subtitle !== undefined && step.subtitle.length > 0 ? (
          <p className='text-xs leading-relaxed text-muted-foreground'>
            {step.subtitle}
          </p>
        ) : null}
        {hasChildren ? (
          <ul
            className='mt-2 ml-1 space-y-2 border-l border-border pl-3'
            role='list'
          >
            {step.children!.map((child) => (
              <ProgressStepRow key={child.id} step={child} nested />
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  )
}

function StepIcon({ status }: { status: ExtractionProgressStepStatus }) {
  if (status === 'done') {
    return (
      <span className='inline-flex size-5 items-center justify-center rounded-full bg-primary/15 text-primary'>
        <Check className='size-3.5' aria-hidden />
      </span>
    )
  }
  if (status === 'active' || status === 'waiting') {
    return (
      <span className='inline-flex size-5 items-center justify-center rounded-full border-2 border-primary bg-background'>
        <Loader2 className='size-3.5 animate-spin text-primary' aria-hidden />
      </span>
    )
  }
  return (
    <span className='inline-flex size-5 items-center justify-center text-muted-foreground'>
      <Circle className='size-4' aria-hidden />
    </span>
  )
}

export function patchExtractionStep(
  steps: ExtractionProgressStep[],
  id: string,
  patch: Partial<Pick<ExtractionProgressStep, 'status' | 'subtitle'>>
): ExtractionProgressStep[] {
  return steps.map((step) => (step.id === id ? { ...step, ...patch } : step))
}
